import { useState } from "react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePoll } from "@/hooks/usePoll";
import { api, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/common";
import { CrudActions, AddButton, confirmDelete, FormDialog, TextField, Field } from "@/components/Crud";
import { ChangePasswordCard } from "@/pages/Profile";

const EMPTY = { name: "", email: "", role: "waiter", password: "", active: true };

export default function Accounts() {
  const { user } = useAuth();
  const { data, reload } = usePoll("/users", 0);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openForm = (u) => { setEdit(u || {}); setForm(u ? { ...u, password: "" } : EMPTY); };
  const save = async () => {
    const body = { name: form.name, email: form.email, role: form.role, active: form.active, password: form.password || null };
    if (edit.id) await api.put(`/users/${edit.id}`, body);
    else await api.post("/users", body);
    toast.success("Akun disimpan");
    reload();
  };

  return (
    <div>
      <PageHeader title="Pengaturan Akun" subtitle="Kelola akun staf dan role aksesnya">
        <AddButton testId="add-user-btn" label="Tambah Akun" onClick={() => openForm()} />
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Panel className="overflow-x-auto xl:col-span-2">
          <Table data-testid="users-table">
            <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data || []).map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-semibold">{u.name}{u.id === user.id && <span className="ml-2 text-xs text-orange-600">(Anda)</span>}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><span className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-semibold text-orange-700">{ROLE_LABEL[u.role]}</span></TableCell>
                  <TableCell className="text-sm">{u.active ? "Aktif" : <span className="text-red-600">Nonaktif</span>}</TableCell>
                  <TableCell>
                    {u.id === user.id ? (
                      <div className="flex justify-end"><button data-testid={`edit-user-${u.email}`} onClick={() => openForm(u)} className="rounded-md px-3 py-1.5 text-sm hover:bg-stone-100">Ubah</button></div>
                    ) : (
                      <CrudActions id={`user-${u.email}`} onEdit={() => openForm(u)} onDelete={() => confirmDelete(`/users/${u.id}`, reload, "akun")} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
        <ChangePasswordCard />
      </div>
      <FormDialog testId="user-form-dialog" open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Ubah Akun" : "Tambah Akun"} onSubmit={save}>
        <TextField label="Nama" testId="user-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} />
        <TextField label="Email" testId="user-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
        <Field label="Role">
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <TextField label={edit?.id ? "Password baru (kosongkan jika tidak diubah)" : "Password"} testId="user-password-input" type="password" required={!edit?.id} minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
        <div className="flex items-center gap-3"><Switch data-testid="user-active-switch" checked={form.active} onCheckedChange={(v) => set("active", v)} /><span className="text-sm">Akun aktif</span></div>
      </FormDialog>
    </div>
  );
}
